import React from 'react';
import { Heart, Target, Shield, TreePine } from 'lucide-react';

const About: React.FC = () => {
  const values = [
    {
      icon: Heart,
      title: 'Passion du bois',
      description: 'Une véritable passion pour les matériaux nobles et le travail artisanal traditionnel.',
    },
    {
      icon: Target,
      title: 'Précision',
      description: 'Chaque projet est réalisé avec le plus grand soin et une attention aux détails.',
    },
    {
      icon: Shield,
      title: 'Fiabilité',
      description: 'Un service de qualité, des délais respectés et un suivi personnalisé.',
    },
    {
      icon: TreePine,
      title: 'Durabilité',
      description: 'Des matériaux sélectionnés pour leur qualité et leur respect de l\'environnement.',
    },
  ];

  return (
    <section id="apropos" className="py-20 bg-white">
      <div className="container-max section-padding">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image et infos artisan */}
          <div className="space-y-6">
            <div className="aspect-[4/5] bg-gradient-to-br from-stone-100 to-stone-200 rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="/images/jeremy.png" 
                alt="Jérémy ARS - Artisan menuisier fondateur d'ARS Agencement"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Informations clés */}
            <div className="bg-sage-50 p-6 rounded-lg border border-sage-200">
              <h3 className="font-bold text-stone-900 mb-4">En quelques chiffres</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-bold text-wood-600">15+</div>
                  <div className="text-sm text-stone-600">Années d'expérience</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-sage-600">200+</div>
                  <div className="text-sm text-stone-600">Projets réalisés</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-600">100%</div>
                  <div className="text-sm text-stone-600">Clients satisfaits</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-wood-600">5★</div>
                  <div className="text-sm text-stone-600">Note moyenne</div>
                </div>
              </div>
            </div>
          </div>

          {/* Contenu texte */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6">
                L'artisan derrière <span className="text-wood-600">ARS Agencement</span>
              </h2>
              <div className="space-y-4 text-lg text-stone-600 leading-relaxed">
                <p>
                  <strong className="text-stone-900">Jérémy ARS</strong>, artisan menuisier passionné, 
                  j'ai fondé ARS Agencement avec une vision claire : créer des espaces uniques qui 
                  reflètent la personnalité de mes clients tout en s'harmonisant avec 
                  l'environnement naturel du Morbihan.
                </p>
                <p>
                  Depuis plus de 15 ans, je puise mon inspiration dans la beauté des paysages 
                  morbihannais : la douceur des galets du Golfe, la noblesse des bois flottés, 
                  les teintes naturelles de notre région. Cette connexion avec la nature guide 
                  chacune de mes créations.
                </p>
                <p>
                  Mon approche privilégie l'écoute, la qualité et l'authenticité. Chaque projet 
                  est une collaboration étroite avec mes clients, de la conception à la réalisation, 
                  pour donner vie à leurs rêves d'agencement.
                </p>
              </div>
            </div>

            {/* Valeurs */}
            <div>
              <h3 className="text-2xl font-bold text-stone-900 mb-6">Mes valeurs</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {values.map((value, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-wood-100 rounded-lg flex items-center justify-center">
                      <value.icon className="w-6 h-6 text-wood-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-stone-900 mb-2">{value.title}</h4>
                      <p className="text-stone-600">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Call to action */}
            <div className="bg-wood-50 p-6 rounded-lg border border-wood-200">
              <h4 className="font-bold text-stone-900 mb-2">
                Prêt à donner vie à votre projet ?
              </h4>
              <p className="text-stone-600 mb-4">
                Discutons ensemble de vos idées d'aménagement. Je vous accompagne 
                de la conception à la réalisation.
              </p>
              <button 
                className="btn-primary"
                onClick={() => {
                  const section = document.getElementById('contact');
                  section?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Planifier un rendez-vous
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
